// Quoted strings in straight, curly, Czech low-high and guillemet quotes. A straight single quote
// counts only outside a word, so an apostrophe ("car's") is kept.
const QUOTED_TEXT_PATTERN =
  /"[^"]*"|“[^”]*”|„[^“"]*[“"]|‚[^‘]*‘|‘[^’]*’|«[^»]*»|(?<![\p{L}\p{N}])'[^']*'(?![\p{L}\p{N}])/gu;

// A text phrase and everything after it up to the next comma, period, semicolon or the end.
// Longer phrases come first, so "with the text" wins over "text" at the same position.
// A negated mention ("no text", "without any text", "text-free", "žádný text") asks for no text,
// so it is kept; "bez textu" and "bez nápisu" do not match because of the word ending.
const TEXT_PHRASE_PATTERN =
  /(?<!(?:^|[^\p{L}\p{N}])(?:no|without|without any|žádný)\s+)(?<![\p{L}\p{N}])(?:with the words|with the word|with the letters|with the text|with text|that says|which says|saying|labeled|s nápisem|s textem|nápis|text)(?![\p{L}\p{N}]|-free)[^,.;]*/giu;

// Connectors left dangling before a punctuation mark or the end once the text is removed.
const DANGLING_CONNECTOR_PATTERN = /(?:^|\s+)(?:and|an|a|the|with|se|s)(?=\s*(?:[,.;]|$))/giu;

const removeDanglingConnectors = (prompt: string): string => {
  const cleanedPrompt = prompt.replace(DANGLING_CONNECTOR_PATTERN, '');
  return cleanedPrompt === prompt ? prompt : removeDanglingConnectors(cleanedPrompt);
};

/**
 * Removes the text the user asks to render (quoted strings, "with the text …", "s nápisem …")
 * from an image prompt and returns the trimmed rest. The image model renders explicitly requested
 * text even when the style prefix forbids it. Returns an empty string when the prompt asks only
 * for text. Prompt Bridge sends the result to the model; DMS uses it to warn before generation.
 */
export function sanitizeImagePrompt(prompt: string): string {
  const withoutText = prompt.replace(QUOTED_TEXT_PATTERN, ' ').replace(TEXT_PHRASE_PATTERN, ' ');

  if (withoutText === prompt) {
    return prompt.trim();
  }

  const sanitizedPrompt = removeDanglingConnectors(
    withoutText
      .replace(/\s+/g, ' ')
      .replace(/\s+([,.;])/g, '$1')
      .replace(/([,.;])[,.;]+/g, '$1')
  )
    .replace(/^[\s,.;]+|[\s,;]+$/g, '')
    .trim();

  return /[\p{L}\p{N}]/u.test(sanitizedPrompt) ? sanitizedPrompt : '';
}
